import { lockGuard, loginGuard, loginWorkspaceGuard } from "src/router/guards/technician/auth"
import { canPerformCorrectiveWork, canPerformRegularWork } from "src/router/guards/technician/work"
import { UUID_REGEX } from "src/util/constants/configurations"
import { NamedPath } from "src/util/enums/namedPaths"
import { RouteRecordRaw } from "vue-router"

/**
 * All the routes that are available to a technician.
 *
 * We have an entry per route that specifies the *full path* in the `path` field, so no subrouting.
 * This has some benefits:
 *    - We can use the appropriate layout for each route.
 *    - We can adjust permissions on the route level. That is, we can be very granular.
 *    - There's a single spot where all the details of a route can be seen (parameters, permissions, name, layout).
 *      This way we can be confident when we want to modify routes.
 *
 *  We always name uuid's that are captured in the path properly, so we know what they refer to.
 *  For instance, prefer `:workspaceId` over the generic `:id`.
 */
const routesTechnician: RouteRecordRaw[] = [
  //////// SETTINGS ////////
  {
    path: "/settings",
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.USER_SETTINGS,
        component: () => import("src/pages/technician/settings/UserSettings.vue"),
      },
    ],
  },

  //////// TODO ////////
  {
    path: `/to-dos/:unsubmittedWorkId${UUID_REGEX}?`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.TO_DO,
        component: () => import("src/pages/technician/to-do/ToDoContainer.vue"),
        props: (route) => ({ unsubmittedWorkTabActive: Boolean(route.params.unsubmittedWorkId) }),
      },
    ],
  },

  //////// AUTH ////////
  {
    path: "/auth",
    component: () => import("src/layouts/technician/AuthLayout.vue"),
    children: [
      {
        path: "",
        name: NamedPath.INITIATE_APP,
        component: () => import("src/pages/technician/InitiateApp.vue"),
        beforeEnter: [loginWorkspaceGuard],
      },
    ],
  },
  {
    path: "/auth/lock",
    component: () => import("src/layouts/technician/AuthLayout.vue"),
    children: [
      {
        path: "",
        name: NamedPath.LOCK,
        component: () => import("src/pages/technician/auth/LockScreen.vue"),
        beforeEnter: [loginGuard],
      },
    ],
  },

  //////// WORKSPACE  ////////
  {
    path: "/workspaces",
    component: () => import("src/layouts/technician/WorkspaceSelectLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.SELECT_WORKSPACE,
        component: () => import("src/pages/technician/workspace/WorkspaceSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/WorkspaceLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.WORKSPACE,
        component: () => import("src/pages/technician/workspace/WorkspaceHome.vue"),
      },
    ],
  },

  //////// ITEMS ////////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/items`,
    component: () => import("src/layouts/technician/GenericSearchLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.SELECT_ITEM,
        component: () => import("src/pages/technician/item/ItemSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/items/:itemId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.ITEM,
        component: () => import("src/pages/technician/item/ItemContainer.vue"),
      },
    ],
  },

  //////// LAYOUTS ////////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/layouts`,
    component: () => import("src/layouts/technician/GenericSearchLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.SELECT_LAYOUT,
        component: () => import("src/pages/technician/layout/LayoutSelect.vue"),
      },
    ],
  },

  //////// MODELS ////////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/models`,
    component: () => import("src/layouts/technician/GenericSearchLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.SELECT_MODEL,
        component: () => import("src/pages/technician/model/ModelSelect.vue"),
      },
    ],
  },

  //////// PERFORMED WORK ////////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/corrective`,
    redirect: { name: NamedPath.SELECT_PERFORMED_WORK },
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/regular`,
    redirect: { name: NamedPath.SELECT_PERFORMED_WORK },
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work`,
    component: () => import("src/layouts/technician/GenericSearchLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.SELECT_PERFORMED_WORK,
        component: () => import("src/pages/technician/work/PerformedWorkSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/corrective/:performedWorkId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.PERFORMED_CORRECTIVE_WORK,
        component: () => import("src/pages/technician/work/PerformedCorrectiveContainer.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/corrective/create/ad-hoc/:issueId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard, canPerformCorrectiveWork],
    children: [
      {
        path: "",
        name: NamedPath.ADHOC_CORRECTIVE_WORK,
        component: () => import("src/pages/technician/work/AdhocCorrectiveContainer.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/corrective/create/planned/:issueId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.PLANNED_CORRECTIVE_WORK,
        component: () => import("src/pages/technician/work/PlannedCorrectiveContainer.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/regular/:performedWorkId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.PERFORMED_REGULAR_WORK,
        component: () => import("src/pages/technician/work/PerformedRegularContainer.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/regular/create/ad-hoc/:itemId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard, canPerformRegularWork],
    children: [
      {
        path: "",
        name: NamedPath.ADHOC_REGULAR_WORK,
        component: () => import("src/pages/technician/work/AdhocRegularContainer.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/regular/create/planned/:itemId${UUID_REGEX}/:workOrderId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.PLANNED_REGULAR_WORK,
        component: () => import("src/pages/technician/work/PlannedRegularContainer.vue"),
      },
    ],
  },

  //////// WORK ORDERS ////////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders/corrective`,
    redirect: { name: NamedPath.SELECT_WORK_ORDER },
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders/regular`,
    redirect: { name: NamedPath.SELECT_WORK_ORDER },
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders`,
    component: () => import("src/layouts/technician/GenericSearchLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.SELECT_WORK_ORDER,
        component: () => import("src/pages/technician/work-order/WorkOrderSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders/corrective/:workOrderId${UUID_REGEX}/:itemId${UUID_REGEX}?`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.WORK_ORDER_CORRECTIVE,
        component: () => import("src/pages/technician/work-order/WorkOrderCorrectiveContainer.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders/regular/:workOrderId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.WORK_ORDER_REGULAR,
        component: () => import("src/pages/technician/work-order/WorkOrderRegularContainer.vue"),
      },
    ],
  },

  //////// ZONES ////////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/zones`,
    component: () => import("src/layouts/technician/GenericSearchLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.SELECT_ZONE,
        component: () => import("src/pages/technician/zone/ZoneSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/zones/:zoneId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.ZONE,
        component: () => import("src/pages/technician/zone/ZoneContainer.vue"),
      },
    ],
  },

  //////// AREAS ////////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/areas`,
    component: () => import("src/layouts/technician/GenericSearchLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.SELECT_AREA,
        component: () => import("src/pages/technician/area/AreaSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/areas/:areaId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.AREA,
        component: () => import("src/pages/technician/area/AreaContainer.vue"),
      },
    ],
  },

  //////// ISSUES ////////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/issues`,
    component: () => import("src/layouts/technician/GenericSearchLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.SELECT_ISSUE,
        component: () => import("src/pages/technician/issue/IssueSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/issues/:issueId${UUID_REGEX}`,
    component: () => import("src/layouts/technician/GenericLayout.vue"),
    beforeEnter: [loginGuard, lockGuard],
    children: [
      {
        path: "",
        name: NamedPath.ISSUE,
        component: () => import("src/pages/technician/issue/IssueOverview.vue"),
      },
    ],
  },
]

export default routesTechnician
